import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  RadialBarChart,
  RadialBar
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  Scale,
  PiggyBank,
  Receipt,
  Wallet
} from 'lucide-react';

// Color palette
const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  pink: '#ec4899',
  slate: '#64748b'
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#ef4444', '#64748b'];

// Format currency
const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value?.toLocaleString() || 0}`;
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
    cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30'
  };

  const iconColors = {
    blue: 'text-blue-400',
    green: 'text-green-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    purple: 'text-purple-400',
    cyan: 'text-cyan-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-4 relative overflow-hidden`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          <span>{Math.abs(trendValue || trend).toFixed(1)}%</span>
          <span className="text-slate-500">vs período anterior</span>
        </div>
      )}
    </div>
  );
};

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
        <p className="text-slate-300 text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Financial Dashboard Component
const FinancialDashboard = ({ financialData }) => {
  // Calculate financial metrics
  const metrics = useMemo(() => {
    const income = financialData?.income || {};
    const costs = financialData?.costs || {};
    const expenses = financialData?.expenses || {};
    const assets = financialData?.assets || {};
    const liabilities = financialData?.liabilities || {};
    const equity = financialData?.equity || {};

    // Income
    const salesWithIva = income.sales_with_iva || 0;
    const salesExempt = income.sales_exempt || 0;
    const otherIncome = income.other_income || 0;
    const totalIncome = salesWithIva + salesExempt + otherIncome;

    // Costs
    const costOfSales = costs.cost_of_sales || 0;
    const purchasesWithIva = costs.purchases_with_iva || 0;
    const purchasesExempt = costs.purchases_exempt || 0;
    const totalCosts = costOfSales + purchasesWithIva + purchasesExempt;

    // Expenses
    const totalExpenses = Object.values(expenses).reduce((sum, val) => sum + (val || 0), 0);

    // Margins
    const grossMargin = totalIncome - totalCosts;
    const grossMarginPct = totalIncome > 0 ? (grossMargin / totalIncome) * 100 : 0;
    const netIncome = grossMargin - totalExpenses;
    const netMarginPct = totalIncome > 0 ? (netIncome / totalIncome) * 100 : 0;

    // Assets & Liabilities
    const totalAssets = Object.values(assets).reduce((sum, val) => sum + (val || 0), 0);
    const totalLiabilities = Object.values(liabilities).reduce((sum, val) => sum + (val || 0), 0);
    const totalEquity = Object.values(equity).reduce((sum, val) => sum + (val || 0), 0);

    // Ratios
    const currentRatio = totalLiabilities > 0 ? totalAssets / totalLiabilities : 0;
    const debtRatio = totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;

    // IVA
    const ivaDebito = salesWithIva * 0.19;
    const ivaCredito = purchasesWithIva * 0.19;
    const ivaNeto = ivaDebito - ivaCredito;

    return {
      totalIncome,
      totalCosts,
      totalExpenses,
      grossMargin,
      grossMarginPct,
      netIncome,
      netMarginPct,
      totalAssets,
      totalLiabilities,
      totalEquity,
      currentRatio,
      debtRatio,
      ivaDebito,
      ivaCredito,
      ivaNeto,
      expenses,
      income,
      costs
    };
  }, [financialData]);

  // Data for Income vs Costs vs Expenses bar chart
  const incomeVsCostsData = useMemo(() => [
    { name: 'Ingresos', value: metrics.totalIncome, fill: COLORS.success },
    { name: 'Costos', value: metrics.totalCosts, fill: COLORS.warning },
    { name: 'Gastos', value: metrics.totalExpenses, fill: COLORS.danger },
    { name: 'Utilidad', value: Math.max(0, metrics.netIncome), fill: COLORS.primary }
  ], [metrics]);

  // Data for expense breakdown pie chart
  const expenseBreakdownData = useMemo(() => {
    const expenses = metrics.expenses;
    const expenseLabels = {
      salaries: 'Sueldos',
      rent: 'Arriendo',
      utilities: 'Servicios',
      marketing: 'Marketing',
      professional_services: 'Profesionales',
      office_supplies: 'Suministros',
      depreciation: 'Depreciación',
      financial_expenses: 'Financieros',
      other_expenses: 'Otros'
    };
    
    return Object.entries(expenses)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        name: expenseLabels[key] || key,
        value: value
      }))
      .sort((a, b) => b.value - a.value);
  }, [metrics.expenses]);

  // Data for income breakdown
  const incomeBreakdownData = useMemo(() => {
    const income = metrics.income;
    return [
      { name: 'Ventas IVA', value: income.sales_with_iva || 0 },
      { name: 'Ventas Exentas', value: income.sales_exempt || 0 },
      { name: 'Otros', value: income.other_income || 0 }
    ].filter(item => item.value > 0);
  }, [metrics.income]);

  // Data for balance structure
  const balanceData = useMemo(() => [
    { name: 'Activos', value: metrics.totalAssets, fill: COLORS.primary },
    { name: 'Pasivos', value: metrics.totalLiabilities, fill: COLORS.danger },
    { name: 'Patrimonio', value: metrics.totalEquity, fill: COLORS.success }
  ], [metrics]);

  // IVA data
  const ivaData = useMemo(() => [
    { name: 'IVA Débito', value: metrics.ivaDebito, fill: COLORS.warning },
    { name: 'IVA Crédito', value: metrics.ivaCredito, fill: COLORS.success },
    { name: 'IVA Neto', value: Math.abs(metrics.ivaNeto), fill: metrics.ivaNeto >= 0 ? COLORS.danger : COLORS.primary }
  ], [metrics]);

  // Margin gauge data
  const marginGaugeData = useMemo(() => [
    { name: 'Margen', value: Math.max(0, Math.min(100, metrics.grossMarginPct)), fill: COLORS.success },
  ], [metrics.grossMarginPct]);

  // Check if we have enough data to show charts
  const hasIncomeData = metrics.totalIncome > 0;
  const hasExpenseData = expenseBreakdownData.length > 0;
  const hasBalanceData = metrics.totalAssets > 0 || metrics.totalLiabilities > 0;

  if (!hasIncomeData && !hasExpenseData && !hasBalanceData) {
    return (
      <div className="bg-slate-800/30 rounded-xl p-8 text-center border border-slate-700/50">
        <Scale className="w-12 h-12 mx-auto text-slate-500 mb-4" />
        <h3 className="text-lg font-medium text-slate-300 mb-2">Ingrese datos financieros</h3>
        <p className="text-sm text-slate-500">
          Complete el formulario de datos financieros para ver el dashboard visual con gráficos interactivos.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          title="Ingresos Totales"
          value={formatCurrency(metrics.totalIncome)}
          icon={DollarSign}
          color="green"
        />
        <KPICard
          title="Margen Bruto"
          value={`${metrics.grossMarginPct.toFixed(1)}%`}
          subtitle={formatCurrency(metrics.grossMargin)}
          icon={TrendingUp}
          color={metrics.grossMarginPct >= 30 ? 'green' : metrics.grossMarginPct >= 15 ? 'amber' : 'red'}
        />
        <KPICard
          title="Utilidad Neta"
          value={formatCurrency(metrics.netIncome)}
          subtitle={`${metrics.netMarginPct.toFixed(1)}% margen`}
          icon={metrics.netIncome >= 0 ? PiggyBank : AlertTriangle}
          color={metrics.netIncome >= 0 ? 'blue' : 'red'}
        />
        <KPICard
          title="IVA Neto"
          value={formatCurrency(Math.abs(metrics.ivaNeto))}
          subtitle={metrics.ivaNeto >= 0 ? 'A pagar' : 'A favor'}
          icon={Receipt}
          color={metrics.ivaNeto >= 0 ? 'amber' : 'cyan'}
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income vs Costs Bar Chart */}
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
          <h3 className="text-sm font-medium text-slate-300 mb-4">Resumen Financiero</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={incomeVsCostsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" tickFormatter={formatCurrency} stroke="#64748b" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={70} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {incomeVsCostsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expense Breakdown Pie Chart */}
        {hasExpenseData && (
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-4">Distribución de Gastos</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={expenseBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {expenseBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Secondary Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Income Breakdown */}
        {incomeBreakdownData.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-4">Composición Ingresos</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={incomeBreakdownData}
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    dataKey="value"
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {incomeBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={[COLORS.success, COLORS.cyan, COLORS.purple][index]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Balance Structure */}
        {hasBalanceData && (
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-4">Estructura Patrimonial</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={balanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={10} />
                  <YAxis tickFormatter={formatCurrency} stroke="#64748b" fontSize={10} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {balanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* IVA Summary */}
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
          <h3 className="text-sm font-medium text-slate-300 mb-4">Resumen IVA (19%)</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ivaData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" tickFormatter={formatCurrency} stroke="#64748b" fontSize={10} />
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} width={70} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {ivaData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Financial Ratios */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 text-center">
          <p className="text-xs text-slate-400 uppercase">Razón Corriente</p>
          <p className={`text-2xl font-bold mt-1 ${metrics.currentRatio >= 1.5 ? 'text-green-400' : metrics.currentRatio >= 1 ? 'text-amber-400' : 'text-red-400'}`}>
            {metrics.currentRatio.toFixed(2)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {metrics.currentRatio >= 1.5 ? 'Saludable' : metrics.currentRatio >= 1 ? 'Aceptable' : 'Riesgo'}
          </p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 text-center">
          <p className="text-xs text-slate-400 uppercase">Endeudamiento</p>
          <p className={`text-2xl font-bold mt-1 ${metrics.debtRatio <= 50 ? 'text-green-400' : metrics.debtRatio <= 70 ? 'text-amber-400' : 'text-red-400'}`}>
            {metrics.debtRatio.toFixed(1)}%
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {metrics.debtRatio <= 50 ? 'Bajo' : metrics.debtRatio <= 70 ? 'Moderado' : 'Alto'}
          </p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 text-center">
          <p className="text-xs text-slate-400 uppercase">Margen Bruto</p>
          <p className={`text-2xl font-bold mt-1 ${metrics.grossMarginPct >= 40 ? 'text-green-400' : metrics.grossMarginPct >= 20 ? 'text-amber-400' : 'text-red-400'}`}>
            {metrics.grossMarginPct.toFixed(1)}%
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {metrics.grossMarginPct >= 40 ? 'Excelente' : metrics.grossMarginPct >= 20 ? 'Bueno' : 'Bajo'}
          </p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 text-center">
          <p className="text-xs text-slate-400 uppercase">Margen Neto</p>
          <p className={`text-2xl font-bold mt-1 ${metrics.netMarginPct >= 15 ? 'text-green-400' : metrics.netMarginPct >= 5 ? 'text-amber-400' : 'text-red-400'}`}>
            {metrics.netMarginPct.toFixed(1)}%
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {metrics.netMarginPct >= 15 ? 'Excelente' : metrics.netMarginPct >= 5 ? 'Aceptable' : 'Bajo'}
          </p>
        </div>
      </div>

      {/* Health Indicator */}
      <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/30 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {metrics.netIncome > 0 && metrics.grossMarginPct >= 20 && metrics.currentRatio >= 1 ? (
              <>
                <div className="p-2 bg-green-500/20 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-green-400">Salud Financiera: Buena</p>
                  <p className="text-xs text-slate-400">Los indicadores muestran una situación financiera estable</p>
                </div>
              </>
            ) : metrics.netIncome >= 0 ? (
              <>
                <div className="p-2 bg-amber-500/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-amber-400">Salud Financiera: Atención</p>
                  <p className="text-xs text-slate-400">Algunos indicadores requieren monitoreo</p>
                </div>
              </>
            ) : (
              <>
                <div className="p-2 bg-red-500/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="font-medium text-red-400">Salud Financiera: Crítica</p>
                  <p className="text-xs text-slate-400">Se detectan pérdidas, requiere acción inmediata</p>
                </div>
              </>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">Patrimonio Neto</p>
            <p className="text-lg font-bold">{formatCurrency(metrics.totalAssets - metrics.totalLiabilities)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;
